import { CircularProgress, Grid, InputAdornment, MenuItem, Select } from '@material-ui/core';
import { ExpandMore } from '@material-ui/icons';
import {
  getGoals,
  getJobs,
  getJobsDomains,
  getStudentDesiredContractLength,
  getStudentGoal,
  getStudentJobDomain,
  getStudentJobSectors,
  setStudentDesiredContractLength,
  setStudentGoal,
  setStudentJobDomain,
  setStudentJobSectors,
} from '@webapps/business';
import {
  Autocomplete,
  FormikAutocompletePx,
  useAuth,
  useMutation,
  useQuery,
  useToast,
} from '@webapps/reactjs-tech-components';
import { useStyles } from '@webapps/styles';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import { useEffect, useState, useRef, useMemo, useLayoutEffect } from 'react';
import { FormattedMessage } from 'react-intl';

import theme from './theme';

const {
  colors: { background, subTextColor },
} = theme;

const Criteria = () => {
  const {
    classes,
    css,
    global,
    cx,
    theme: { spacing },
  } = useStyles(styles);

  const { idUser } = useAuth();
  const { addToast } = useToast();
  const [jobs, setJobs] = useState([]);
  const [pageJob, setPageJob] = useState(1);
  const [toRefetch, setToRefetch] = useState(false);
  const listboxRef = useRef(null);
  const savedScrollTop = useRef(0);
  const scrollAnchorRef = useRef(null);

  const [searchString, setSearchString] = useState('');

  const contractLength = ['1', '2', '3', '4', '6', '12', '24'];

  const { data: { content: jobsList = [], totalElements } = {}, isFetching: isLoadingJobs } = useQuery(getJobs, {
    key: ['jobs', searchString, pageJob],
    variables: { 
      keyword: searchString || '',
      page: pageJob - 1,
      },
    keepPreviousData: true,
  });

  const memoizedJobs = useMemo(() => jobs, [jobs]);

  useEffect(() => {
    setJobs((prevJobs) => [...prevJobs, ...jobsList]);
  }, [jobsList]);

  useEffect(() => {
    if (toRefetch && jobs.length < totalElements) {
      setPageJob(prev => prev + 1);
    }
  }, [toRefetch]); 
  
  const getScrollAnchor = () => {
    if (!listboxRef.current) return null;
    const { scrollTop, clientHeight } = listboxRef.current;
    const items = listboxRef.current.querySelectorAll('[role="option"]');
    
    // Find first item below scroll midpoint
    let anchor = null;
    items.forEach(item => {
      if (item.offsetTop + item.offsetHeight/2 > scrollTop + clientHeight/2) {
        if (!anchor) anchor = item;
      }
    });
    
    return anchor;
  };

  const handleScroll = (event) => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    savedScrollTop.current = scrollTop;
    scrollAnchorRef.current = getScrollAnchor();

    const shouldLoad = scrollTop + clientHeight >= scrollHeight - 200 && jobs.length < totalElements;

    if (shouldLoad) {
      setToRefetch(true);
    }
  };

  useLayoutEffect(() => {
    if (!listboxRef.current || !scrollAnchorRef.current) return;

    const restore = () => {
      const anchor = scrollAnchorRef.current;
      if (anchor && anchor.offsetParent) { // Ensure element is still in DOM
        const newPosition = anchor.offsetTop - (listboxRef.current.clientHeight/2);
        listboxRef.current.scrollTop = newPosition;
      } else {
        listboxRef.current.scrollTop = savedScrollTop.current;
      }
    };

    // Wait for Autocomplete's virtualization to settle
    const raf = requestAnimationFrame(() => {
      restore();
      setToRefetch(false);
      setTimeout(restore, 50); 
    });

    return () => cancelAnimationFrame(raf);
  }, [jobs]);

  const { data: jobsDomain = [] } = useQuery(getJobsDomains, {
    key: 'jobsDomain',
  });

  const formik = useFormik({
    initialValues: {
      keyword: searchString || '',
    },
    onSubmit: ({ keyword }) => (
      setJobs([]),
      setPageJob(1),
      setSearchString(keyword)
    )
  });

  const handleSelectedValue = (value) => {
    if (value.length <= 3) {
      const jobs = value?.map((item) => item?.ogr);
      handleSectors(jobs);
    } else {
      addToast({
        status: 'error',
        message: 'Vous ne pouvez pas sélectionner plus de 3 métiers',
      });
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setSearchString(formik.values.keyword);
    }, 300);

    return () => {
      if (formik.values.keyword !== searchString)
        clearTimeout(handler);
    };
  }, [formik.values]);

  useEffect(() => {
    if(formik.values.keyword) {
      setJobs([]);
      setPageJob(1);
    }
  }, [formik.values.keyword]);
  
  const { data: goals = [] } = useQuery(getGoals, {
    key: 'allGoals',
  });

  const {
    data: myGoal = [],
    refetch: refetchMyGoal,
    isLoading: isLoadingMyGoal,
  } = useQuery(getStudentGoal, {
    key: ['myGoal', idUser],
    variables: { id: idUser },
  });

  const {
    data: myLength = [],
    refetch: refetchMyLength,
    isLoading: isLoadingMyLength,
  } = useQuery(getStudentDesiredContractLength, {
    key: ['myLength', idUser],
    variables: { id: idUser },
  });

  const {
    data: myDomain = [],
    refetch: refetchMyDomain,
    isLoading: isLoadingMyDomain,
  } = useQuery(getStudentJobDomain, {
    key: ['myDomain', idUser],
    variables: { id: idUser },
  });

  const {
    data: mySectors = [],
    refetch: refetchMySectors,
    isLoading: isLoadingMySectors,
  } = useQuery(getStudentJobSectors, {
    key: ['mySectors', idUser],
    variables: { id: idUser },
  });

  const {
    isLoading: isLoadingSetGoal,
    isSuccess: isSuccessSetGoal,
    mutateAsync: mutateSetStatus,
  } = useMutation(setStudentGoal);

  const {
    isLoading: isLoadingSetLength,
    isSuccess: isSuccessSetLength,
    mutateAsync: mutateSetLength,
  } = useMutation(setStudentDesiredContractLength);

  const {
    isLoading: isLoadingSetDomain,
    isSuccess: isSuccessSetDomain,
    mutateAsync: mutateSetDomain,
  } = useMutation(setStudentJobDomain);

  const {
    isLoading: isLoadingSetSectors,
    isSuccess: isSuccessSetSectors,
    mutateAsync: mutateSetSectors,
  } = useMutation(setStudentJobSectors);

  useEffect(() => {
    if (
      isSuccessSetGoal ||
      isSuccessSetLength ||
      isSuccessSetDomain ||
      isSuccessSetSectors
    ) {
      refetchMyGoal();
      refetchMyLength();
      refetchMyDomain();
      refetchMySectors();
      addToast({
        status: 'success',
        message: 'Opération réussie',
      });
    }
  }, [
    isSuccessSetGoal,
    isSuccessSetLength,
    isSuccessSetDomain,
    isSuccessSetSectors,
  ]);

  // useEffect(() => {
  //   if (isSuccessAllGoal) {
  //     refetchMyGoal();
  //   }
  // }, [isSuccessAllGoal]);

  const handleChange = async (e) => {
    await mutateSetStatus({
      id: idUser,
      idGoal: e?.id,
    });
  };

  const handleLength = async (e) => {
    await mutateSetLength({
      id: idUser,
      length: e,
    });
  };

  const handleDomain = async (e) => {
    await mutateSetDomain({
      id: idUser,
      domain: e?.id,
    });
  };

  const handleSectors = async (value) => {
    await mutateSetSectors({
      id: idUser,
      sectors: value,
    });
  };

  return (
    <div className={classes.container}>
      <div className={cx(global.title, classes.title)}>
        <div>
          <FormattedMessage id="student.profil.criteria.title" />
        </div>
        {/* <div className={css({ marginLeft: 'auto' })}>
          <IconButton onClick={handleOpenEditDescription} size="small" style={{ margin: 3, borderRadius: '20px', border: '1px solid #C0D9FF', backgroundColor: '#F3F7FF', width: '30px', display: 'flex', justifyContent: 'center', height: '30px', alignItems: 'center', cursor: 'pointer' }}>
            <EditIcon fontSize="small" />
          </IconButton>
        </div> */}
      </div>
      <div style={{ marginTop: '8px' }} className={global.subTitle}>
        <FormattedMessage id="student.profil.criteria.subTitle" />
      </div>
      <Grid
        container
        spacing={2}
        className={css({
          marginTop: 20,
          '& > .MuiGrid-item': { padding: '10px' },
        })}
      >
        <Grid item xs={12} lg={6} style={{ width: '100%' }} >
          <FormikAutocompletePx
            formik={formik}
            borderRadius={6}
            fullWidth
            completed={myGoal}
            getOptionLabel={(option) => option.label || ''}
            classes={{
              inputRoot: css({
                backgroundColor: myGoal ? '#F3F7FF' : '#fbd3d5',
                borderRadius: 6,
                // border: myGoal ? '1px solid #C0D9FF' : '1px solid #FBD3D5',
              }),
            }}
            label="Mon parcours souhaité"
            name="label"
            options={goals}
            pathValue="id"
            onChange={(event, value) => handleChange(value)}
            value={myGoal}
          />
        </Grid>
        <Grid className={classes.myLength} item xs={12} lg={6} style={{ width: '100%' }} >
          <FormikAutocompletePx
            formik={formik}
            borderRadius={6}
            fullWidth
            completed={myLength}
            getOptionLabel={(option) => (option ? `${option} mois` : '')}
            classes={{
              inputRoot: css({
                backgroundColor: myLength ? '#F3F7FF' : '#fbd3d5',
                borderRadius: 6,
                // border: myLength ? '1px solid #C0D9FF' : '1px solid #FBD3D5',
              }),
            }}
            label="Durée de contrat de stage ou d'alternance souhaitée"
            name="label"
            options={contractLength}
            pathValue="id"
            onChange={(event, value) => handleLength(value)}
            value={myLength || ''}
          />
        </Grid>
        <Grid item xs={12} lg={6} style={{ width: '100%' }} >
          <FormikAutocompletePx
            formik={formik}
            borderRadius={6}
            fullWidth
            completed={myDomain}
            getOptionLabel={(option) => option.label || ''}
            classes={{
              inputRoot: css({
                backgroundColor: myDomain ? '#F3F7FF' : '#fbd3d5',
                borderRadius: 6,
                // border: myDomain ? '1px solid #C0D9FF' : '1px solid #FBD3D5',
              }),
            }}
            label="Domaine professionnel souhaité"
            name="label"
            options={jobsDomain}
            pathValue="id"
            onChange={(event, value) => handleDomain(value)}
            value={myDomain}
          />
        </Grid>
        <Grid item xs={12} lg={6} style={{ width: '100%' }} >
          <FormikAutocompletePx
            formik={formik}
            borderRadius={6}
            fullWidth
            completed={mySectors.length > 0}
            getOptionLabel={(option) => option.label || ''}
            classes={{
              inputRoot: css({
                backgroundColor: mySectors?.length > 0 ? '#F3F7FF' : '#fbd3d5',
                borderRadius: 6,
                // border: mySectors?.length > 0 ? '1px solid #C0D9FF' : '1px solid #FBD3D5',
              }),
            }}
            label="Intitulés de postes souhaités"
            name="label"
            options={memoizedJobs}
            pathValue="id"
            multiple
            filterSelectedOptions
            onChange={(event, value) => {
              handleSelectedValue(value);
            }}
            onInputChange={(event, value) => {
              formik.setFieldValue('keyword', value);
            }}
            value={mySectors}
            endAdornment={isLoadingJobs && toRefetch && <InputAdornment position='end'><CircularProgress size={20} /></InputAdornment>}
            ListboxProps={{
              ref: listboxRef,
              onScroll: handleScroll
            }}
          />
        </Grid>
      </Grid>
    </div>
  );
};

const styles = ({ spacing }) => ({
  container: {
    background,
    borderRadius: 10,
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    // minHeight: 340,
    height: '100%',
  },
  title: {
    color: '#152652',
    display: 'flex',
  },
  input: {
    backgroundColor: '#FFFFFF',
  },
  myLength: {
    '@media (max-width:1200px)': {
      marginTop: spacing(0) + ' !important',
    },
  },
});

Criteria.propTypes = {
  presentation: PropTypes.string,
  refetch: PropTypes.func,
};

export default Criteria;
